/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: PriorityRoutingFormView.tsx
 🗓️ Created: 2025-12-10
 🧩 Description:
    Form view for creating/editing Priority Routing entries
 ────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowUp, Search, Plus, Loader2, Settings, Route, Copy } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Toggle } from '../../components/ui/Toggle';
import ModuleDestinationSelect from '../../components/ui/ModuleDestinationSelect';
import { FormBox } from '../../components/ui/FormBox';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { FixedActionBar } from '../../components/ui/FixedActionBar';
import { ModuleFormHeader } from '../../components/layouts';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { useNotifications } from '../../contexts/NotificationContext';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import {
    getPriorityRoutingById,
    createPriorityRouting,
    updatePriorityRouting,
    deletePriorityRouting,
    fetchPriorityRouting,
    PriorityRoutingEntry,
    CreatePriorityRoutingPayload,
} from './api';

interface FormData {
    name: string;
    module: string;
    destination: string;
    priority: number;
    enabled: boolean;
}

const defaultFormData: FormData = {
    name: '',
    module: '',
    destination: '',
    priority: 5,
    enabled: true,
};

export default function PriorityRoutingFormView() {
    const { id } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();
    const duplicateId = searchParams.get('duplicate');
    const navigate = useNavigate();
    const { t } = useTranslation('priorityRouting');
    const { showSuccess, showError } = useNotifications();

    const isEditMode = id && id !== 'new';

    // Form state
    const [formData, setFormData] = useState<FormData>(defaultFormData);
    const [originalFormData, setOriginalFormData] = useState<FormData>(defaultFormData);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    // Delete modal
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // Quick search modal
    const [showQuickList, setShowQuickList] = useState(false);
    const [quickListItems, setQuickListItems] = useState<PriorityRoutingEntry[]>([]);
    const [isLoadingQuickList, setIsLoadingQuickList] = useState(false);

    // Load data when editing or duplicating, reset when creating new
    useEffect(() => {
        if (isEditMode && id) {
            loadData(id, false);
        } else if (duplicateId) {
            loadData(duplicateId, true);
        } else {
            // Reset form when navigating to new
            setFormData(defaultFormData);
            setOriginalFormData(defaultFormData);
            setErrors({});
        }
    }, [id, duplicateId]);

    const loadData = async (entryId: string, isDuplicate: boolean = false) => {
        setIsLoading(true);
        try {
            const entry = await getPriorityRoutingById(entryId);
            if (entry) {
                const data: FormData = {
                    name: isDuplicate ? `${entry.name} (Copy)` : entry.name,
                    module: entry.module || '',
                    destination: entry.destination || '',
                    priority: entry.priority,
                    enabled: entry.enabled,
                };
                setFormData(data);
                // For duplicates, keep originalFormData as default so it's considered "dirty"
                setOriginalFormData(isDuplicate ? defaultFormData : data);
            } else {
                showError(t('loadError', 'Entry not found'));
                navigate('/pbx/call-center/priority-routing');
            }
        } catch (error) {
            showError(error instanceof Error ? error.message : t('loadError', 'Failed to load'));
            navigate('/pbx/call-center/priority-routing');
        } finally {
            setIsLoading(false);
        }
    };

    // Validate form
    const validateForm = useCallback(() => {
        const newErrors: Record<string, string> = {};

        if (!formData.name.trim()) {
            newErrors.name = t('nameRequired', 'Name is required');
        }

        if (formData.priority < 0 || formData.priority > 10) {
            newErrors.priority = 'Priority must be between 0 and 10';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [formData, t]);

    // Handle change
    const handleChange = useCallback(<K extends keyof FormData>(field: K, value: FormData[K]) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // Clear error when field is updated
        if (errors[field]) {
            setErrors(prev => {
                const next = { ...prev };
                delete next[field];
                return next;
            });
        }
    }, [errors]);

    // Save handler
    const handleSave = useCallback(async (): Promise<PriorityRoutingEntry | undefined> => {
        if (!validateForm()) {
            showError(t('common:validationError', 'Please fix the errors before saving'));
            return undefined;
        }

        setIsSaving(true);
        try {
            const payload: CreatePriorityRoutingPayload = {
                name: formData.name.trim(),
                module: formData.module,
                destination: formData.destination,
                priority: formData.priority,
                enabled: formData.enabled,
            };

            let result: PriorityRoutingEntry;
            if (isEditMode && id) {
                result = await updatePriorityRouting(id, payload);
                showSuccess(t('updateSuccess', 'Priority routing updated successfully'));
            } else {
                result = await createPriorityRouting(payload);
                showSuccess(t('createSuccess', 'Priority routing created successfully'));
            }

            setOriginalFormData(formData);
            return result;
        } catch (error) {
            showError(error instanceof Error ? error.message : t('saveError', 'Failed to save'));
            return undefined;
        } finally {
            setIsSaving(false);
        }
    }, [formData, isEditMode, id, validateForm, showSuccess, showError, t]);

    // Use save with action hook
    const handleSaveWithAction = useSaveWithAction({
        onSave: handleSave,
        listPath: '/pbx/call-center/priority-routing',
        newPath: '/pbx/call-center/priority-routing/new',
        mode: isEditMode ? 'edit' : 'create',
        getEditPath: (savedData) => `/pbx/call-center/priority-routing/${(savedData as any).uuid || savedData.id}`,
        getFormData: () => formData,
        storageKey: 'priorityRouting:saveAndNewData',
    });

    // Cancel handler - Resets form to original state
    const handleCancel = useCallback(() => {
        setFormData(originalFormData);
        setErrors({});
    }, [originalFormData]);

    // Delete handlers
    const handleDeleteClick = useCallback(() => {
        setShowDeleteModal(true);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        if (!id) return;

        setIsDeleting(true);
        try {
            await deletePriorityRouting(id);
            showSuccess(t('deleteSuccess', 'Priority routing deleted successfully'));
            navigate('/pbx/call-center/priority-routing');
        } catch (error) {
            showError(error instanceof Error ? error.message : t('deleteError', 'Failed to delete'));
        } finally {
            setIsDeleting(false);
            setShowDeleteModal(false);
        }
    }, [id, navigate, showSuccess, showError, t]);

    // Quick list handlers
    const loadQuickListItems = useCallback(async () => {
        setIsLoadingQuickList(true);
        try {
            const result = await fetchPriorityRouting({ page: 1, limit: 100, sortBy: 'priority', sortOrder: 'desc' });
            setQuickListItems(result.data);
        } catch (error) {
            setQuickListItems([]);
        } finally {
            setIsLoadingQuickList(false);
        }
    }, []);

    useEffect(() => {
        if (showQuickList && quickListItems.length === 0) {
            loadQuickListItems();
        }
    }, [showQuickList, quickListItems.length, loadQuickListItems]);

    // Navigation handlers
    const handleGoToList = useCallback(() => {
        navigate('/pbx/call-center/priority-routing');
    }, [navigate]);

    const handleAddNew = useCallback(() => {
        navigate('/pbx/call-center/priority-routing/new');
    }, [navigate]);

    // Duplicate handler
    const handleDuplicateFromForm = useCallback(() => {
        if (!isEditMode || !id) return;
        navigate(`/pbx/call-center/priority-routing/new?duplicate=${id}`);
    }, [id, isEditMode, navigate]);

    // Priority color
    const getPriorityColor = (priority: number) => {
        if (priority >= 8) return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
        if (priority >= 5) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="p-6 pb-20">
            <ModuleFormHeader
                icon={<ArrowUp className="h-5 w-5 text-primary" />}
                title={isEditMode ? `${t('editTitle', 'Edit Priority Routing')} - ${formData.name || ''}` : t('createTitle', 'New Priority Routing')}
                backButtonText={t('common:list', 'List')}
                onBack={handleGoToList}
                actions={[
                    // 1. Quick Search (always visible)
                    {
                        icon: <Search className="h-5 w-5 text-muted-foreground" />,
                        tooltip: t('priorityRouting:actions.quickList'),
                        onClick: () => setShowQuickList(true),
                    },
                    // 2. Duplicate (edit mode only)
                    ...(isEditMode
                        ? [{
                            icon: <Copy className="h-5 w-5 text-muted-foreground" />,
                            tooltip: t('priorityRouting:actions.duplicate'),
                            onClick: handleDuplicateFromForm,
                        }]
                        : []),
                    // 3. Add New (always visible)
                    {
                        icon: <Plus className="h-5 w-5 text-muted-foreground" />,
                        tooltip: t('priorityRouting:actions.add'),
                        onClick: handleAddNew,
                    },
                ]}
            />

            {/* Form Content - Using same layout as Extensions */}
            <div className="mt-8 space-y-4">
                {/* Priority Routing Information */}
                <FormBox
                    title={t('title', 'Priority Routing')}
                    icon={<Settings className="h-5 w-5" />}
                >
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                        {/* Row 1: Name | Input | Priority | Slider+Badge */}
                        <SimpleTooltip
                            content={t('nameTooltip', 'A unique name to identify this priority rule')}
                            title={t('name', 'Name')}
                            position="top"
                            maxWidth="300px"
                        >
                            <Label className="text-left">
                                {t('name', 'Name')} <span className="text-destructive">*</span>
                            </Label>
                        </SimpleTooltip>
                        <Input
                            value={formData.name}
                            onChange={(e) => handleChange('name', e.target.value)}
                            placeholder={t('namePlaceholder', 'e.g., VIP Customers')}
                            error={errors.name}
                        />




                        {/* Row 3: Priority | Slider+Badge */}
                        <SimpleTooltip
                            content={t('priorityTooltip', 'A higher priority means this rule will be evaluated sooner (0-10, 10 being highest)')}
                            title={t('priority', 'Priority')}
                            position="top"
                            maxWidth="300px"
                        >
                            <Label className="text-left">
                                {t('priority', 'Priority')} (0-10)
                            </Label>
                        </SimpleTooltip>
                        <div className="flex items-center gap-3">
                            <input
                                type="range"
                                min="0"
                                max="10"
                                value={formData.priority}
                                onChange={(e) => handleChange('priority', parseInt(e.target.value))}
                                className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                            />
                            <span className={`inline-flex items-center justify-center w-10 h-10 rounded-full text-lg font-bold ${getPriorityColor(formData.priority)}`}>
                                {formData.priority}
                            </span>
                        </div>

                        {/* Row 4: Enabled | Toggle */}
                        <SimpleTooltip
                            content={t('enabledTooltip', 'Enable or disable this priority rule')}
                            title={t('common:enabled', 'Enabled')}
                            position="top"
                            maxWidth="300px"
                        >
                            <Label className="text-left">
                                {t('common:enabled', 'Enabled')}
                            </Label>
                        </SimpleTooltip>
                        <Toggle
                            value={formData.enabled}
                            onChange={(value) => handleChange('enabled', value)}
                        />
                        {/* Empty cells for grid alignment */}
                        <div></div>
                        <div></div>
                    </div>
                </FormBox>

                {/* Routing Box */}
                <FormBox
                    title={t('routing', 'Routing')}
                    icon={<Route className="h-5 w-5" />}
                >
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                        <SimpleTooltip
                            content={t('destinationTooltip', 'Select where to route the call based on this priority rule')}
                            title={t('destination', 'Route To')}
                            position="top"
                            maxWidth="300px"
                        >
                            <Label className="text-left">
                                {t('destination', 'Route To')}
                            </Label>
                        </SimpleTooltip>
                        <div className="col-span-3">
                            <ModuleDestinationSelect
                                moduleValue={formData.module}
                                destinationValue={formData.destination}
                                onModuleChange={(value) => handleChange('module', value)}
                                onDestinationChange={(value) => handleChange('destination', value)}
                                modulePlaceholder={t('selectModule', 'Select Module')}
                                destinationPlaceholder={t('selectDestination', 'Select Destination')}
                            />
                        </div>
                    </div>
                </FormBox>
            </div>

            {/* Fixed Action Bar */}
            <FixedActionBar
                onSaveWithAction={handleSaveWithAction}
                onCancel={handleCancel}
                onDelete={isEditMode ? handleDeleteClick : undefined}
                isEditMode={!!isEditMode}
                isSaving={isSaving}
                cancelText={t('common:cancel', 'Cancel')}
                deleteText={t('common:delete', 'Delete')}
                useSaveDropdown={true}
                saveDropdownStorageKey="priorityRouting:saveAction"
                saveDisabled={isSaving}
            />

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleDeleteConfirm}
                title={t('deleteTitle', 'Delete Priority Routing')}
                message={`${t('deleteConfirm', 'Are you sure you want to delete')} "${formData.name}"?`}
                confirmText={t('common:delete', 'Delete')}
                cancelText={t('common:cancel', 'Cancel')}
                type="danger"
                isLoading={isDeleting}
            />

            {/* Quick Search Modal */}
            <QuickSearchModal
                isOpen={showQuickList}
                onClose={() => setShowQuickList(false)}
                title={t('title', 'Priority Routing')}
                items={quickListItems}
                isLoadingInitial={isLoadingQuickList}
                isSearching={false}
                searchValue=""
                onSearchChange={() => { }}
                onItemSelect={(item) => {
                    setShowQuickList(false);
                    navigate(`/pbx/call-center/priority-routing/${(item as any).uuid || item.id}`);
                }}
                currentItemId={id}
                renderItem={(item, isCurrent) => (
                    <button
                        onClick={() => {
                            if (!isCurrent) {
                                setShowQuickList(false);
                                navigate(`/pbx/call-center/priority-routing/${(item as any).uuid || item.id}`);
                            }
                        }}
                        disabled={isCurrent}
                        className={`w-full text-left p-3 rounded-md transition-colors ${isCurrent
                            ? 'bg-primary/20 border-2 border-primary cursor-default'
                            : 'hover:bg-gray-100 dark:hover:bg-gray-700 border border-border'
                            }`}
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <ArrowUp className="h-4 w-4 text-primary flex-shrink-0" />
                                <div>
                                    <span className="font-semibold text-foreground">{item.name}</span>
                                    {item.destination && (
                                        <div className="text-sm text-muted-foreground truncate">
                                            {item.module ? `${item.module}: ` : ''}{item.destination}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(item.priority)}`}>
                                {item.priority}
                            </span>
                        </div>
                    </button>
                )}
                searchPlaceholder={t('common:search', 'Search...')}
                emptyMessage={t('common:noResults', 'No items found')}
                noResultsMessage={t('common:noResults', 'No items found')}
                headerIcon={<ArrowUp className="h-5 w-5" />}
            />
        </div>
    );
}
